// frontend/src/components/Events/EditSectionCoverModal.js
import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  LinearProgress,
  Paper,
  Grid,
  alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { sectionsAPI } from '../../services/eventsAPI';
import { styled } from '@mui/material/styles';

const MAX_IMAGES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_FORMATS = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': { padding: theme.spacing(2) },
  '& .MuiDialog-paper': {
    width: 'min(760px, calc(100vw - 32px))',
    maxWidth: 760,
    minWidth: 0,
    height: 'min(82vh, 760px)',
    maxHeight: 'calc(100vh - 32px)',
    margin: 0,
    overflow: 'hidden',
    borderRadius: theme.spacing(4.5),
    border: `1px solid ${alpha(theme.palette.common.white, 0.84)}`,
    background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.78)}, ${alpha(theme.palette.grey[100], 0.50)} 58%, ${alpha(theme.palette.primary.main, 0.055)}), radial-gradient(circle at 92% 8%, ${alpha(theme.palette.primary.main, 0.11)}, transparent 34%)`,
    backgroundColor: alpha(theme.palette.common.white, 0.62),
    backdropFilter: 'blur(30px) saturate(150%)',
    WebkitBackdropFilter: 'blur(30px) saturate(150%)',
    boxShadow: `0 28px 90px ${alpha(theme.palette.common.black, 0.22)}`,
  },
  '& .MuiBackdrop-root': {
    backgroundColor: alpha(theme.palette.common.black, 0.32),
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(2.2, 2.6),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.60)}, ${alpha(theme.palette.common.white, 0.18)})`,
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
}));

const GlassSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.8),
  borderRadius: theme.spacing(2.8),
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.54)}, ${alpha(theme.palette.common.white, 0.20)})`,
  border: `1px solid ${alpha(theme.palette.common.white, 0.76)}`,
  backdropFilter: 'blur(14px) saturate(130%)',
  WebkitBackdropFilter: 'blur(14px) saturate(130%)',
  boxShadow: `0 7px 22px ${alpha(theme.palette.common.black, 0.045)}`,
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.68rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}));

const ThumbnailContainer = styled(Paper)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 122,
  borderRadius: theme.spacing(2.4),
  overflow: 'hidden',
  cursor: 'pointer',
  border: `1px solid ${alpha(theme.palette.common.white, 0.82)}`,
  background: alpha(theme.palette.grey[100], 0.55),
  boxShadow: `0 7px 20px ${alpha(theme.palette.common.black, 0.07)}`,
  transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    borderColor: alpha(theme.palette.primary.main, 0.36),
    boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.10)}`,
    '& .delete-overlay': { opacity: 1 }
  },
  '&.selected': {
    borderColor: alpha(theme.palette.primary.main, 0.62),
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.18)}, 0 12px 28px ${alpha(theme.palette.primary.main, 0.12)}`
  }
}));

const ThumbnailImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block'
});

const DeleteOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(180deg, rgba(0,0,0,0.10), rgba(0,0,0,0.48))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.18s ease',
  zIndex: 2
});

const UploadZone = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  borderRadius: theme.spacing(2.8),
  border: `1.5px dashed ${alpha(theme.palette.primary.main, 0.28)}`,
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.48)}, ${alpha(theme.palette.primary.main, 0.045)})`,
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  transition: 'all 0.18s ease',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.55),
    backgroundColor: alpha(theme.palette.primary.main, 0.055),
    transform: 'translateY(-1px)'
  }
}));

const getFullImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `https://fincollab.ru${path}`;
};

const checkImageOrientation = (file) => {
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        const isLandscape = img.width > img.height;
        const isSquare = Math.abs(img.width - img.height) < 10;
        resolve(isLandscape || isSquare);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

const EditSectionCoverModal = ({ open, onClose, sectionId, currentImages = [], onSave }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    if (files.length + acceptedFiles.length > MAX_IMAGES) {
      setError(`Максимум ${MAX_IMAGES} изображений`);
      return;
    }
    if (rejectedFiles.length > 0) {
      const errors = rejectedFiles.map(f => {
        if (f.file.size > MAX_FILE_SIZE) return `${f.file.name}: файл слишком большой (макс. 5MB)`;
        return `${f.file.name}: недопустимый формат (JPEG, PNG, WEBP, GIF)`;
      });
      setError(errors.join('. '));
      return;
    }
    setError('');
    for (const file of acceptedFiles) {
      const isValid = await checkImageOrientation(file);
      if (!isValid) {
        setError(`Изображение "${file.name}" должно быть горизонтальным или квадратным`);
        return;
      }
    }
    setFiles(prev => [...prev, ...acceptedFiles]);
  }, [files]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/gif': ['.gif']
    },
    maxSize: MAX_FILE_SIZE,
    maxFiles: MAX_IMAGES - files.length
  });

  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveImage = async (imageUrl) => {
    if (!window.confirm('Удалить это изображение?')) return;
    try {
      const newImages = currentImages.filter(url => url !== imageUrl);
      await sectionsAPI.updateCoverImages(sectionId, newImages);
      onSave(newImages);
      if (selectedImage === imageUrl) setSelectedImage(null);
    } catch (err) {
      console.error('Ошибка удаления:', err);
      setError('Не удалось удалить изображение');
    }
  };

  const handleSetAsCurrent = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setError('');
    try {
      const uploadedUrls = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('image', file);
        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
        const response = await sectionsAPI.uploadCoverImage(
          sectionId,
          formData,
          (progress) => setUploadProgress(prev => ({ ...prev, [file.name]: progress }))
        );
        uploadedUrls.push(response.url);
        setUploadProgress(prev => {
          const next = { ...prev };
          delete next[file.name];
          return next;
        });
      }
      const allImages = [...currentImages, ...uploadedUrls];
      await sectionsAPI.updateCoverImages(sectionId, allImages);
      onSave(allImages);
      setFiles([]);
    } catch (err) {
      console.error('Ошибка загрузки:', err);
      setError('Не удалось загрузить изображения');
    } finally {
      setUploading(false);
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" fontWeight="700" sx={{ color: 'primary.main', lineHeight: 1.15 }}>
            Обложка секции
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.55 }}>
            Управление изображениями и текущей обложкой
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            width: 40, height: 40, flexShrink: 0,
            color: 'text.secondary',
            backgroundColor: alpha('#fff', 0.44),
            border: `1px solid ${alpha('#fff', 0.80)}`,
            '&:hover': {
              color: 'primary.main',
              backgroundColor: alpha('#fff', 0.68)
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <DialogContent
        sx={{
          p: 2.2,
          pt: 0,
          overflow: 'auto',
          minHeight: 0,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: alpha('#000', 0.14),
            borderRadius: 999,
            '&:hover': { background: alpha('#000', 0.22) }
          }
        }}
      >
        <Box sx={{ mt: 2 }}>
          {currentImages.length > 0 && (
            <GlassSection sx={{ mb: 1.6 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1.3 }}>
                <Box>
                  <SectionLabel sx={{ mb: 0.35 }}>Текущие изображения</SectionLabel>
                  <Typography variant="body2" color="text.secondary">
                    {currentImages.length} из {MAX_IMAGES}
                  </Typography>
                </Box>
                <Box sx={{
                  px: 1.1, py: 0.45, borderRadius: 999,
                  backgroundColor: alpha('#fff', 0.48),
                  border: `1px solid ${alpha('#fff', 0.72)}`,
                  color: 'text.secondary', fontSize: '0.72rem', fontWeight: 650
                }}>
                  Нажмите на изображение, чтобы выбрать
                </Box>
              </Box>
              <Grid container spacing={1.4}>
                {currentImages.map((url, index) => (
                  <Grid item xs={6} sm={4} key={index}>
                    <ThumbnailContainer
                      className={selectedImage === url ? 'selected' : ''}
                      onClick={() => handleSetAsCurrent(url)}
                    >
                      <ThumbnailImage
                        src={getFullImageUrl(url)}
                        alt={`Обложка ${index + 1}`}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                      <DeleteOverlay className="delete-overlay">
                        <IconButton
                          size="small"
                          sx={{
                            color: '#fff',
                            backgroundColor: 'rgba(255,255,255,0.20)',
                            border: '1px solid rgba(255,255,255,0.35)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.34)' }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage(url);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </DeleteOverlay>
                      {selectedImage === url && (
                        <Box sx={{
                          position: 'absolute', top: 8, left: 8,
                          width: 27, height: 27, borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: 'primary.main', color: '#fff',
                          border: '2px solid rgba(255,255,255,0.9)',
                          zIndex: 3
                        }}>
                          <CheckCircleIcon sx={{ fontSize: 17 }} />
                        </Box>
                      )}
                    </ThumbnailContainer>
                  </Grid>
                ))}
              </Grid>
            </GlassSection>
          )}

          <UploadZone
            {...getRootProps()}
            sx={{
              opacity: uploading || files.length >= MAX_IMAGES ? 0.62 : 1,
              cursor: uploading || files.length >= MAX_IMAGES ? 'not-allowed' : 'pointer',
              mb: 1.6,
              '&:hover': uploading || files.length >= MAX_IMAGES ? {} : undefined
            }}
          >
            <input {...getInputProps()} />
            <Box sx={{
              width: 54, height: 54, borderRadius: '50%', mx: 'auto', mb: 1.2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: alpha('#fff', 0.58),
              border: `1px solid ${alpha('#fff', 0.82)}`,
              color: 'primary.main'
            }}>
              <UploadIcon sx={{ fontSize: 27 }} />
            </Box>
            <Typography variant="subtitle1" fontWeight="700" gutterBottom>
              {isDragActive ? 'Отпустите файлы здесь' : 'Добавьте изображения'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Перетащите файлы сюда или нажмите для выбора
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.2 }}>
              Горизонтальные или квадратные · JPEG, PNG, WEBP, GIF · до 5 MB
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block">
              Максимум {MAX_IMAGES} изображений
            </Typography>
          </UploadZone>

          {Object.keys(uploadProgress).length > 0 && (
            <GlassSection sx={{ mb: 1.6 }}>
              <SectionLabel>Загрузка</SectionLabel>
              {Object.entries(uploadProgress).map(([fileName, progress]) => (
                <Box key={fileName} sx={{ mb: 1.2, '&:last-child': { mb: 0 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: '75%' }}>{fileName}</Typography>
                    <Typography variant="body2" fontWeight="650">{progress}%</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 6, borderRadius: 999,
                      backgroundColor: alpha('#000', 0.06),
                      '& .MuiLinearProgress-bar': { borderRadius: 999 }
                    }}
                  />
                </Box>
              ))}
            </GlassSection>
          )}

          {error && (
            <Alert
              severity="error"
              onClose={() => setError('')}
              sx={{
                mb: 1.6, borderRadius: 2.5,
                backgroundColor: alpha('#fff', 0.58),
                border: `1px solid ${alpha('#f44336', 0.16)}`
              }}
            >
              {error}
            </Alert>
          )}

          {files.length > 0 && (
            <GlassSection sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.3 }}>
                <SectionLabel sx={{ mb: 0 }}>Новые изображения</SectionLabel>
                <Box sx={{
                  px: 1, py: 0.35, borderRadius: 999,
                  bgcolor: alpha('#fff', 0.48),
                  border: `1px solid ${alpha('#fff', 0.72)}`,
                  fontSize: '0.72rem', fontWeight: 650, color: 'text.secondary'
                }}>
                  {files.length}
                </Box>
              </Box>
              <Grid container spacing={1.4}>
                {files.map((file, index) => (
                  <Grid item xs={6} sm={4} key={index}>
                    <ThumbnailContainer>
                      <ThumbnailImage src={URL.createObjectURL(file)} alt={file.name} />
                      <DeleteOverlay className="delete-overlay">
                        <IconButton
                          size="small"
                          sx={{
                            color: '#fff',
                            backgroundColor: 'rgba(255,255,255,0.20)',
                            border: '1px solid rgba(255,255,255,0.35)'
                          }}
                          onClick={() => handleRemoveFile(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </DeleteOverlay>
                    </ThumbnailContainer>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.65, px: 0.3 }}>
                      {file.name}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </GlassSection>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: 2.2, py: 1.5, gap: 1,
        borderTop: `1px solid ${alpha('#fff', 0.72)}`,
        backgroundColor: alpha('#fff', 0.20),
        flexShrink: 0
      }}>
        <Button
          onClick={onClose}
          sx={{ borderRadius: 999, px: 2.5, textTransform: 'none', color: 'text.secondary' }}
        >
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={files.length === 0 || uploading}
          startIcon={uploading ? <CircularProgress size={19} /> : <CheckCircleIcon />}
          sx={{
            borderRadius: 999, px: 2.8, textTransform: 'none',
            boxShadow: `0 7px 20px ${alpha('#000', 0.12)}`
          }}
        >
          {uploading ? 'Загрузка...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default EditSectionCoverModal;
